/**
 * Fase 3: clientes, fits (IA × perfil do cliente), triagem da equipe e ficha do edital.
 * Consultas à API, filtros da fila de triagem (na URL) e formatação.
 */
import type {
  Cliente,
  ClienteInput,
  ClientesResponse,
  EditalFichaResponse,
  Ficha,
  FitItem,
  FitsResponse,
  IaStatusResponse,
  TRIAGEM_DECISOES,
} from '@licita/contracts';
import { FIT_DECISOES } from '@licita/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from './api';
import { queryClient } from './queries';

// Os contratos exportam só os schemas das respostas; o tipo sai do parse.
type ClientesResponse = ReturnType<typeof ClientesResponse.parse>;
type FitsResponse = ReturnType<typeof FitsResponse.parse>;
type EditalFichaResponse = ReturnType<typeof EditalFichaResponse.parse>;
type IaStatusResponse = ReturnType<typeof IaStatusResponse.parse>;

// ---------- clientes ----------

export const clientesQuery = queryOptions({
  queryKey: ['clientes'],
  queryFn: () => api<ClientesResponse>('/admin/clientes'),
});

export const clienteQuery = (id: string) =>
  queryOptions({ queryKey: ['clientes', id], queryFn: () => api<Cliente>(`/admin/clientes/${id}`) });

/** POST (novo) ou PUT (edição); devolve o cliente salvo e invalida a lista. */
export const INTERESSE_ROTULO: Record<'participar' | 'nao' | 'ajuda', { rotulo: string; tone: Tone }> = {
  participar: { rotulo: 'cliente: vou participar', tone: 'ok' },
  nao: { rotulo: 'cliente: não me interessa', tone: 'muted' },
  ajuda: { rotulo: 'cliente: quer ajuda', tone: 'warn' },
};

/** Dá acesso ao app a uma pessoa da empresa (só admin). */
export async function darAcesso(clienteId: string, body: { nome: string; email: string }): Promise<Cliente> {
  const c = await api<Cliente>(`/admin/clientes/${clienteId}/acessos`, { method: 'POST', json: body });
  await queryClient.invalidateQueries({ queryKey: ['clientes'] });
  return c;
}

export async function salvarCliente(id: string | null, body: ClienteInput): Promise<Cliente> {
  const c = id
    ? await api<Cliente>(`/admin/clientes/${id}`, { method: 'PUT', json: body })
    : await api<Cliente>('/admin/clientes', { method: 'POST', json: body });
  await queryClient.invalidateQueries({ queryKey: ['clientes'] });
  return c;
}

// ---------- fits e triagem ----------

export const TRIAGENS = {
  pendente: 'Pendentes',
  aprovado: 'Aprovados',
  descartado: 'Descartados',
  adiado: 'Adiados',
  todas: 'Todas',
} as const;
export type TriagemFiltro = keyof typeof TRIAGENS;
export type FitDecisao = (typeof FIT_DECISOES)[number];
export type TriagemDecisao = (typeof TRIAGEM_DECISOES)[number];

/** Filtros da fila de triagem, como ficam na URL (`/triagem?clienteId=...&triagem=todas`). */
export interface FiltrosFits {
  clienteId?: string;
  decisao?: FitDecisao;
  triagem: TriagemFiltro;
  abertos: boolean;
  pagina: number;
  porPagina: number;
}

export const FITS_PADRAO: FiltrosFits = { triagem: 'pendente', abertos: true, pagina: 1, porPagina: 25 };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const flag = (v: unknown, padrao: boolean) => (v === undefined || v === null ? padrao : v === true || v === 'true');
const inteiro = (v: unknown, min: number, max: number, padrao: number) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isInteger(n) && n >= min && n <= max ? n : padrao;
};

export function validarFiltrosFits(raw: Record<string, unknown>): FiltrosFits {
  return {
    clienteId: typeof raw.clienteId === 'string' && UUID.test(raw.clienteId) ? raw.clienteId : undefined,
    decisao:
      typeof raw.decisao === 'string' && (FIT_DECISOES as readonly string[]).includes(raw.decisao)
        ? (raw.decisao as FitDecisao)
        : undefined,
    triagem: typeof raw.triagem === 'string' && raw.triagem in TRIAGENS ? (raw.triagem as TriagemFiltro) : 'pendente',
    abertos: flag(raw.abertos, true),
    pagina: inteiro(raw.pagina, 1, 1_000_000, 1),
    porPagina: inteiro(raw.porPagina, 10, 100, 25),
  };
}

export const fitsQuery = (f: FiltrosFits) =>
  queryOptions({
    queryKey: ['fits', f],
    queryFn: () => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') p.set(k, String(v));
      return api<FitsResponse>(`/admin/fits?${p.toString()}`);
    },
    placeholderData: (prev) => prev,
  });

export const fichaQuery = (editalId: string) =>
  queryOptions({
    queryKey: ['edital', editalId, 'ficha'],
    queryFn: () => api<EditalFichaResponse>(`/admin/editais/${editalId}/ficha`),
  });

/** Registra a triagem e invalida tudo que mostra fits (fila, ficha do edital, contadores dos clientes). */
export async function triar(fitId: string, decisao: TriagemDecisao, observacao: string | null): Promise<void> {
  await api<{ ok: true }>(`/admin/fits/${fitId}/triagem`, { method: 'POST', json: { decisao, observacao } });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['fits'] }),
    queryClient.invalidateQueries({ queryKey: ['edital'] }),
    queryClient.invalidateQueries({ queryKey: ['clientes'] }),
  ]);
}

export const iaQuery = queryOptions({ queryKey: ['ia'], queryFn: () => api<IaStatusResponse>('/admin/ia') });

// ---------- rótulos ----------

type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'muted';

export const DECISAO_FIT: Record<FitDecisao, { rotulo: string; tone: Tone }> = {
  recomendado: { rotulo: 'recomendado', tone: 'ok' },
  possivel: { rotulo: 'possível', tone: 'warn' },
  descartado: { rotulo: 'descartado', tone: 'muted' },
};

export const DECISAO_TRIAGEM: Record<TriagemDecisao, { rotulo: string; tone: Tone }> = {
  aprovado: { rotulo: 'aprovado', tone: 'ok' },
  descartado: { rotulo: 'descartado', tone: 'bad' },
  adiado: { rotulo: 'adiado', tone: 'warn' },
};

export const CATEGORIAS: Record<Ficha['categoria'], string> = {
  software_licenca: 'Licença de software',
  software_desenvolvimento: 'Desenvolvimento de software',
  software_locacao_saas: 'Locação de software / SaaS',
  servico_ti: 'Serviço de TI',
  suporte_manutencao: 'Suporte e manutenção',
  dados_ia: 'Dados e IA',
  infraestrutura_nuvem: 'Infraestrutura e nuvem',
  consultoria: 'Consultoria',
  misto: 'Misto',
  fora_de_ti: 'Fora de TI',
};

export const PRESENCIAL: Record<Ficha['execucao']['presencial'], string> = {
  sim: 'Sim',
  nao: 'Não',
  parcial: 'Parcial',
  nao_informado: 'Não informado',
};

export const STATUS_FICHA: Record<string, { rotulo: string; tone: Tone }> = {
  pendente: { rotulo: 'pendente', tone: 'muted' },
  enviada: { rotulo: 'em processamento', tone: 'info' },
  gerada: { rotulo: 'gerada', tone: 'ok' },
  erro: { rotulo: 'erro', tone: 'bad' },
};

export const STATUS_LOTE: Record<string, { rotulo: string; tone: Tone }> = {
  enviado: { rotulo: 'enviado', tone: 'info' },
  concluido: { rotulo: 'concluído', tone: 'ok' },
  erro: { rotulo: 'erro', tone: 'bad' },
};

/** Nota 0–100 → cor: ≥70 ok, 40–69 atenção, <40 ruim. */
export const notaTone = (nota: number | null | undefined): Tone =>
  nota == null ? 'muted' : nota >= 70 ? 'ok' : nota >= 40 ? 'warn' : 'bad';

export const nomeCliente = (c: Pick<Cliente, 'razaoSocial' | 'nomeFantasia'>) => c.nomeFantasia || c.razaoSocial;

/** Ordena fits por nota (maior primeiro; sem nota por último). */
export const porNota = (fits: FitItem[]) => [...fits].sort((a, b) => (b.nota ?? -1) - (a.nota ?? -1));

// ---------- formatação ----------

/** "12345678000195" → "12.345.678/0001-95" (devolve o original se não tiver 14 dígitos). */
export function cnpjFmt(v: string): string {
  const d = v.replace(/\D/g, '');
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : v;
}

/** "1.234,56" / "1234.56" / "R$ 1.234" → centavos; vazio → null; inválido → undefined. */
export function reaisParaMinor(texto: string): number | null | undefined {
  const t = texto.replace(/[R$\s]/g, '');
  if (!t) return null;
  // Vírgula é a casa decimal ("1.234,56"); sem vírgula, pontos de milhar ("1.200.000") ou decimal ("1234.56").
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
  if (!/^\d+(\.\d{0,2})?$/.test(normal)) return undefined;
  return Math.round(Number(normal) * 100);
}

/** Centavos → "1.234,56" (para preencher o campo). */
export const minorParaReais = (minor: number | null | undefined) =>
  minor == null ? '' : (minor / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const usd = (n: number) => `US$ ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const pct = (x: number) => `${Math.round(x * 100)}%`;
